import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { GroupReach } from './users.ts';

// "Why can this person do that?" and the table of every route's permission (design §6.2, §6.11).

export const PermissionExplanation = z.object({
  userId: z.string(),
  permission: z.string(),
  /** False for a permission no module declares. */
  declared: z.boolean(),
  has: z.boolean(),
  /** Holds everything through ITC_SUPERADMINS, not a group. */
  superadmin: z.boolean(),
  /** The groups that grant it: Administrators for everything, or groups listing it. */
  via: z.array(GroupReach),
});
export type PermissionExplanation = z.infer<typeof PermissionExplanation>;

export const explainPermission = defineEndpoint({
  method: 'GET',
  path: '/admin/permissions/explain',
  permission: 'core.admin.groups',
  query: z.object({ user: z.string().regex(/^[a-z0-9]{15}$/), permission: z.string().min(1).max(128) }),
  response: PermissionExplanation,
});

export const RouteEntry = z.object({
  method: z.string(),
  /** The full URL, including /api. */
  url: z.string(),
  /** `public`, `authenticated`, `discord` (any member, from Discord only), or a declared permission. */
  permission: z.string(),
  module: z.string(),
  recentLogin: z.boolean(),
  idempotencyKey: z.enum(['none', 'optional', 'required']),
  /** False for the worker protocol's routes, whose commands are their own record. */
  audit: z.boolean(),
});
export type RouteEntry = z.infer<typeof RouteEntry>;

export const permissionTable = defineEndpoint({
  method: 'GET',
  path: '/admin/permissions/table',
  permission: 'core.admin.groups',
  response: z.object({
    /** Every route. */
    routes: z.array(RouteEntry),
    permissions: z.array(z.object({ permission: z.string(), description: z.string(), module: z.string(), internal: z.boolean() })),
    /** Every slash command, button and modal of the enabled modules, with the permission it needs. The endpoints it calls check their own. */
    interactions: z.array(z.object({ kind: z.enum(['command', 'button', 'modal']), name: z.string(), module: z.string(), permission: z.string() })),
    /** Every Discord capability, who provides it and which modules call it: which modules touch Discord. */
    capabilities: z.array(z.object({ name: z.string(), module: z.string(), idempotent: z.boolean(), usedBy: z.array(z.string()) })),
    /** Every live topic: who may subscribe, whether it takes a param, and whether a resource rule (an authorizer) also decides. */
    topics: z.array(z.object({ name: z.string(), module: z.string(), permission: z.string(), param: z.boolean(), authorizer: z.boolean(), description: z.string() })),
    /** Every notification kind: its default channels, whether it's critical, its hourly cap, and who sees it in their settings. */
    notifications: z.array(
      z.object({ kind: z.string(), module: z.string(), description: z.string(), defaults: z.array(z.string()), critical: z.boolean(), ratePerHour: z.number().int(), permission: z.string().nullable() }),
    ),
  }),
});
